import { useApiQuery } from "@gladis/api-client";
import type { PropertiesChartDto } from "@gladis/shared";
import { Button, ErrorState, LoadingState, StackedAreaChart, type ChartSeries } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import { useState, type ReactNode } from "react";

const RANGES = [
  { value: "1m", label: "1 month" },
  { value: "3m", label: "3 months" },
  { value: "12m", label: "12 months" },
] as const;

type Range = (typeof RANGES)[number]["value"];

export const propertiesChartKey = (range: Range) => ["properties", "chart", range] as const;

/**
 * How the household's property position has moved.
 *
 * ============================================================================
 * LINES, NOT A STACK — AND THAT IS A CORRECTNESS CHOICE, NOT A STYLE ONE
 * ============================================================================
 *
 * A stacked chart composes: the top of the stack is the whole. These four do NOT compose. Equity is
 * value minus owed, so stacking value on top of equity draws the same money twice and produces a
 * total that means nothing at all — while looking exactly like a total that means something.
 *
 * `variant="lines"` draws each series from zero, so its height is its own value. That is the
 * comparison a household actually wants here: is the gap between what it is worth and what we owe
 * getting wider?
 *
 * ============================================================================
 * TWO OF THESE LINES ARE MEASURED DIFFERENTLY, AND IT SHOWS
 * ============================================================================
 *
 * **Owed** is derived from the amortisation, so it is exact in every past month and moves smoothly.
 *
 * **Value** is whatever was last recorded on or before each month, so it is a STEP: flat until
 * someone records a valuation, then a jump. With one valuation per property it is a straight line —
 * not a bug, just the honest consequence of nobody having valued the house since. The caption says
 * so, because a flat line with no explanation reads as broken.
 *
 * **Gain** is the value line shifted down by what was paid. Same shape, different question.
 */
export function PropertiesChart(): ReactNode {
  const t = useT();
  const [range, setRange] = useState<Range>("12m");
  const chart = useApiQuery<PropertiesChartDto>(
    propertiesChartKey(range),
    `/properties/chart?range=${range}`,
  );

  if (chart.isPending) return <LoadingState>{t("prop.chart.loading")}</LoadingState>;
  if (chart.isError) return <ErrorState>{t("prop.chart.loadFailed")}</ErrorState>;

  const data = chart.data;
  if (!data) return <ErrorState>{t("prop.chart.loadFailed")}</ErrorState>;

  /*
   * Keyed by a stable id distinct from the label — the contract 006 had to fix in this wrapper after
   * two accounts named "Savings" silently merged into one layer. These four ids are fixed strings
   * rather than anything user-supplied, so they cannot collide; the discipline is kept anyway
   * because the next chart's might not be.
   */
  const series: ChartSeries[] = [
    { id: "value", label: t("prop.chart.worth"), points: data.currentValue },
    { id: "owed", label: t("prop.list.colOwed"), points: data.totalOwed },
    { id: "equity", label: t("prop.list.colEquity"), points: data.equity },
    { id: "gain", label: t("prop.list.colGain"), points: data.gain },
  ];

  const valueIsFlat = new Set(data.currentValue.map((point) => point.value)).size <= 1;

  return (
    /*
     * A NAMED REGION, so the four lines can be found without ambiguity.
     *
     * "Owed" is also a column header in the table below and a label on the detail page. An
     * unscoped lookup for it matches three elements — which is exactly how the E2E assertion for
     * this component first failed. The accessible name is the fix for a screen reader and for the
     * test at the same time.
     */
    /*
     * A CARD, like every other section in this application. Its own `<section>` carries the classes
     * rather than being wrapped in `Panel`, because it already owns its heading — and that heading
     * is `aria-labelledby`'d, which an E2E assertion depends on. Wrapping would give it two.
     */
    <section
      aria-labelledby="properties-chart-heading"
      className="rounded-control border-line bg-panel flex flex-col gap-3 border p-5"
    >
      {/*
        `flex-wrap`, AND IT IS THE ONE WORD BETWEEN THIS HEADER AND `Panel`'S.
        
        Three buttons reading "1 month", "3 months" and "12 months" are ~300px of controls. Beside a
        heading, on a 390px phone, `justify-between` with no wrapping does not shrink them — it
        pushes them off the right edge, where nothing clips them and nothing scrolls. The rail's own
        overflow bug was measured the same way and had the same shape.

        The investments range selector is identical and has never done this, because it is `Panel`'s
        `aside` and `Panel`'s header has always been `flex-wrap items-start justify-between gap-3`.
        This component deliberately does not use `Panel` — it owns its heading and its
        `aria-labelledby` — and in copying the row it did not copy that.
      */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="properties-chart-heading" className="text-xl font-semibold">
          {t("prop.chart.heading")}
        </h2>
        {/*
          THE GROUP WRAPS TOO, and the header's own `flex-wrap` is not enough without it.
          
          Three buttons are ~300px of controls on their own. Wrapping the header puts them on a line
          of their own, which is what fixes a 390px phone — and on a 320px one that line is still
          narrower than they are. `Button` carries `shrink-0`, correctly: a control that shrinks to
          fit truncates its own label. So the only thing that can give is the row.

          Measured at both widths rather than reasoned about: at 320 the group alone still overflowed
          with only the header wrapping, which is how this second `flex-wrap` came to be here.
        */}
        <div className="flex flex-wrap gap-2">
          {RANGES.map((option) => (
            <Button
              key={option.value}
              onClick={() => {
                setRange(option.value);
              }}
              variant={option.value === range ? "primary" : "outline"}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </div>

      <StackedAreaChart series={series} variant="lines" emptyMessage={t("prop.chart.empty")} />

      {valueIsFlat ? <p className="text-sm text-muted">{t("prop.chart.flatHint")}</p> : null}
    </section>
  );
}
